// The Ascend dialog: the creature, the stone and level it needs, and what it
// becomes, with a row per requirement (have / need). A creature pays with its own
// stone; a legacy regional item in the bag adds a pick. Rolls carry over.
import { SPECIES } from '../shared/species.ts';
import { ITEM, ascendItems, ascendItemFrom, ascensionStone } from '../shared/items.ts';
import { huntPower } from '../shared/power.ts';
import type { ClientMsg } from '../shared/protocol.ts';
import { S } from './state.ts';
import { spriteUrl, iconUrl } from './assets.ts';
import { creatureArt } from './skins.ts';
import { esc, full, elChip, cname } from './fmt.ts';
import { openDialog } from './dialog.ts';

/** Ready to ascend now: at the level, with a stone it takes in the bag. Shown as a quiet mark, never a pop-up. */
export function ascendReady(c: { species: string; ground: string; level: number; listed?: unknown }): boolean {
  const t = S.trainer, sp = SPECIES[c.species];
  if (!t || c.listed || !sp?.evolvesTo || !sp.evolveLevel || c.level < sp.evolveLevel) return false;
  return ascendItemFrom(c, t.bag) !== null;
}

/** The mark beside a creature's level when it can ascend. */
export const ascendMark = (c: Parameters<typeof ascendReady>[0]) =>
  (ascendReady(c) ? '<span class="asc-dot" title="Can ascend" aria-label="Can ascend"><svg viewBox="0 0 12 12" aria-hidden="true"><path d="M6 1.5 10.5 7H7.6v3.5H4.4V7H1.5z"/></svg></span>' : '');

const ok = (yes: boolean) => `<i class="asc-mark ${yes ? 'yes' : 'no'}" aria-label="${yes ? 'met' : 'not met'}">${yes ? '✓' : '✕'}</i>`;

export function ascendDialog(id: number, send: (m: ClientMsg) => void) {
  const c = S.creatures.get(id), t = S.trainer;
  if (!c || !t) return;
  const sp = SPECIES[c.species], to = sp.evolvesTo ? SPECIES[sp.evolvesTo] : null;
  if (!to || !sp.evolveLevel) return;
  const bag = t.bag, lv = sp.evolveLevel, lvOk = c.level >= lv;
  const own = ascensionStone(c.species);
  // Payable items other than the own stone (a legacy regional item), only those in the bag.
  const alt = ascendItems(c).filter((x) => x !== own && (bag[x] ?? 0) > 0);
  let pick = ascendItemFrom(c, bag) ?? own;
  const toC = { ...c, species: to.id };
  const fig = (x: typeof c, name: string, els: readonly string[]) => `<figure><img src="${spriteUrl(creatureArt(x), x.rolls.aura)}" alt=""><figcaption><b>${esc(name)}</b><span>${els.map(elChip).join('')}</span></figcaption></figure>`;
  const stoneRow = (item: string) => {
    const have = bag[item] ?? 0;
    return `<li class="${have ? 'ok' : 'no'}"><img src="${iconUrl(item)}" alt=""><span><b>${esc(ITEM[item].name)}</b><small>${have ? 'in your bag' : `<a href="#/market" data-dlg="">on the Market</a>`}</small></span><em>${full(have)}/1</em>${ok(have > 0)}</li>`;
  };
  const body = (item: string) => `<div class="asc">
    <div class="asc-stage">${fig(c, cname(c), sp.elements)}
      <div class="asc-mid"><span class="asc-stone"><img src="${iconUrl(item)}" alt=""><em>×1</em></span><span class="asc-arrows" aria-hidden="true">›››</span><small>Lv ${lv}</small></div>
      ${fig(toC, to.name, to.elements)}</div>
    <ul class="asc-req">${stoneRow(item)}
      <li class="${lvOk ? 'ok' : 'no'}"><span class="asc-lv" aria-hidden="true">⬆</span><span><b>Lv ${lv}</b><small>minimum level</small></span><em>Lv ${full(c.level)}</em>${ok(lvOk)}</li></ul>
    ${alt.length ? `<label class="asc-pay">Pay with <select name="item">${[own, ...alt].map((x) => `<option value="${x}"${x === item ? ' selected' : ''}>${esc(ITEM[x].name)} (${full(bag[x] ?? 0)})</option>`).join('')}</select></label>` : ''}
    <p class="asc-pow">Power ${full(huntPower(c))} → <b>${full(huntPower(toC))}</b></p></div>`;
  const ready = (item: string) => lvOk && (bag[item] ?? 0) > 0;
  void openDialog({
    title: 'Ascend',
    body: body(pick),
    actions: [{ label: 'Ascend', value: 'ascend', kind: 'gold', disabled: !ready(pick) }],
    onInput: (root, data) => {
      if (!data.item || data.item === pick) return;
      pick = data.item;
      root.querySelector('.body')!.innerHTML = body(pick);
      const btn = root.querySelector<HTMLButtonElement>('[data-dlg="ascend"]');
      if (btn) btn.disabled = !ready(pick);
    },
  }).then((r) => { if (r.value) send({ t: 'ascend', id, item: pick }); });
}
